import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import FieldConfigurationAPI from "../../../../../lib/api/FieldConfiguration";
import { getActiveCompanyOptions } from "../AccessGroupsList";

function formatUserLabel(user) {
  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return [name || "Unknown user", user?.code, user?.email]
    .filter(Boolean)
    .join(" | ");
}

const normEmail = (u) => (u?.email || "").trim().toLowerCase();

export default function CopyApprovalTeamModal({
  open,
  team,
  companies,
  createApprovalTeam,
  onClose,
  onCopied,
}) {
  const [name, setName] = useState("");
  const [targetCompanyUuid, setTargetCompanyUuid] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectStep, setSelectStep] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUuids, setSelectedUuids] = useState([]);
  const [removedCount, setRemovedCount] = useState(0);

  const targetOptions = getActiveCompanyOptions(
    companies,
    companies?.configureApprovalGroupsData
  );

  useEffect(() => {
    if (!open) return;
    setName(`Copy of ${team?.name || ""}`);
    setTargetCompanyUuid(
      targetOptions.some((opt) => opt.value === team?.company_uuid)
        ? team.company_uuid
        : targetOptions[0]?.value || ""
    );
    setError(null);
    setSelectStep(false);
    setAvailableUsers([]);
    setSelectedUuids([]);
    setRemovedCount(0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const targetCompanyName = () =>
    targetOptions.find((opt) => opt.value === targetCompanyUuid)?.name ||
    "the selected company";

  async function createTeam(trimmedName, memberUuids, removed) {
    const result = await createApprovalTeam(
      { name: trimmedName, company_users: memberUuids },
      targetCompanyUuid
    );
    setIsSaving(false);
    if (!result?.success) {
      setError(
        result?.error || "Failed to create the approval team in the company."
      );
      return;
    }
    onCopied(result.data, targetCompanyName(), removed);
  }

  async function handleCopy() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please enter a name for the copy.");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Name must be 100 characters or fewer.");
      return;
    }
    if (!targetCompanyUuid) {
      setError("Please select a company to copy this approval team to.");
      return;
    }

    setIsSaving(true);
    setError(null);

    const existing = await FieldConfigurationAPI.getApprovalTeams(
      targetCompanyUuid
    );
    if (existing.status !== 200) {
      setIsSaving(false);
      setError(
        "Failed to check for existing approval teams. Please try again."
      );
      return;
    }
    const nameConflict = (existing.data || []).some(
      (t) =>
        t.company_uuid === targetCompanyUuid &&
        t.name?.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (nameConflict) {
      setIsSaving(false);
      setError(
        `An approval team named "${trimmedName}" already exists in ${targetCompanyName()}. Nothing was copied.`
      );
      return;
    }

    const [sourceResp, usersResp, rolesResp] = await Promise.all([
      FieldConfigurationAPI.getApprovalTeam(team.uuid, team.company_uuid),
      FieldConfigurationAPI.getUsers(targetCompanyUuid),
      FieldConfigurationAPI.getRoles(),
    ]);
    if (
      sourceResp.status !== 200 ||
      usersResp.status !== 200 ||
      !Array.isArray(usersResp.data)
    ) {
      setIsSaving(false);
      setError("Failed to load team members. Please try again.");
      return;
    }

    // Approvers need can_approve; if roles can't be read, don't filter.
    const rolesOk = rolesResp.status === 200 && Array.isArray(rolesResp.data);
    const rolesByUuid = {};
    (rolesOk ? rolesResp.data : []).forEach((r) => {
      if (r?.uuid) rolesByUuid[r.uuid] = r;
    });
    const canApprove = (u) =>
      !rolesOk ||
      rolesByUuid[u.role_uuid]?.permissions?.timesheets?.can_approve === true;

    const eligible = usersResp.data.filter(canApprove);
    const eligibleByEmail = {};
    eligible.forEach((u) => {
      if (normEmail(u)) eligibleByEmail[normEmail(u)] = u;
    });

    const sourceMembers = (sourceResp.data?.company_users || []).filter(
      (u) => typeof u !== "string"
    );
    const matched = [];
    sourceMembers.forEach((m) => {
      const match = eligibleByEmail[normEmail(m)];
      if (match && !matched.includes(match.uuid)) matched.push(match.uuid);
    });
    const removed = sourceMembers.length - matched.length;

    if (matched.length === 0) {
      setRemovedCount(removed);
      setAvailableUsers(eligible);
      setSelectedUuids([]);
      setSelectStep(true);
      setIsSaving(false);
      return;
    }

    await createTeam(trimmedName, matched, removed);
  }

  async function handleCreateWithSelection() {
    if (selectedUuids.length === 0) {
      setError("Please select at least one member for the approval team.");
      return;
    }
    setIsSaving(true);
    setError(null);
    await createTeam(name.trim(), selectedUuids, removedCount);
  }

  const toggleSelected = (uuid) =>
    setSelectedUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Copy Approval Team</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          {!selectStep ? (
            <>
              <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
                Creates a new approval team with the same members as{" "}
                <strong>"{team?.name}"</strong>. Members who don't exist in the
                selected company are left out.
              </p>
              <div className="jfm-field">
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  maxLength={100}
                  disabled={isSaving}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="jfm-field">
                <label>Company</label>
                <select
                  value={targetCompanyUuid}
                  disabled={isSaving}
                  onChange={(e) => setTargetCompanyUuid(e.target.value)}
                >
                  {targetOptions.length === 0 && (
                    <option value="">No companies available</option>
                  )}
                  {targetOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
                None of the members of <strong>"{team?.name}"</strong> exist in{" "}
                <strong>{targetCompanyName()}</strong>. Select the members for
                the new team.
              </p>
              <div
                style={{
                  maxHeight: "260px",
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "6px 10px",
                }}
              >
                {availableUsers.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "#64748b" }}>
                    No users in this company can be approvers.
                  </p>
                ) : (
                  availableUsers.map((u) => (
                    <label
                      key={u.uuid}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        flex: "none",
                        gap: "8px",
                        alignItems: "center",
                        margin: 0,
                        padding: "4px 0",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUuids.includes(u.uuid)}
                        disabled={isSaving}
                        onChange={() => toggleSelected(u.uuid)}
                      />
                      {formatUserLabel(u)}
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            <button
              className="jfm-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="jfm-save"
              onClick={selectStep ? handleCreateWithSelection : handleCopy}
              disabled={
                isSaving ||
                targetOptions.length === 0 ||
                (selectStep && selectedUuids.length === 0)
              }
              style={
                selectStep && selectedUuids.length === 0
                  ? { background: "#cbd5e1", color: "#64748b", opacity: 1 }
                  : undefined
              }
            >
              {isSaving ? "Copying..." : selectStep ? "Create Team" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
